import type { ReactNode } from 'react'

// ResponsiveTable — a generic, declarative table that degrades to stacked
// label/value cards below `sm`. The same `columns`/`rows`/`actions` drive two
// sibling DOM trees rendered from one data path: a real `<table className="hidden
// sm:table …">` and a `<ul className="sm:hidden …">`, with Tailwind's responsive
// `display` utilities doing the swap (no JS resize listener, no layout-engine
// dependency — works in the static build).
//
// In table mode each column is a `<th scope="col">`/`<td>` pair; the optional
// `actions` slot is the final right-aligned cell. In stacked mode each row is a
// card: a `primary` column renders full-width as the heading, every other
// non-`hideLabel` column renders as a `label / value` flex row, and `actions`
// renders as a full-width wrapping footer (auto-width buttons that wrap).
//
// The thead/tbody classes byte-match the markup the tabs use today (see
// VersionsTab), so a caller can keep its `<Card className="overflow-hidden p-0">`
// wrapper unchanged. This component renders NO outer Card/border — the caller
// owns the surrounding surface.
export interface Column<Row> {
  header: string // <th> text AND the stacked-card field label
  cell: (row: Row) => ReactNode // full cell content (badges / mono / icons live here)
  align?: 'left' | 'right' // table-mode text-align; ignored in stacked mode
  hideLabel?: boolean // omit the stacked label (icon-only / actions columns)
  primary?: boolean // render full-width as the stacked-card heading row
  thClassName?: string // rare escape hatch for a custom <th> class
}

export interface ResponsiveTableProps<Row> {
  columns: Column<Row>[]
  rows: Row[]
  rowKey: (row: Row) => string
  actions?: (row: Row) => ReactNode // right cell on sm+, full-width wrapping footer when stacked
  emptyState?: ReactNode // optional; most callers keep their own Card empty-state
  className?: string
}

export function ResponsiveTable<Row>({
  columns,
  rows,
  rowKey,
  actions,
  emptyState,
  className = '',
}: ResponsiveTableProps<Row>) {
  if (rows.length === 0 && emptyState !== undefined) {
    return <>{emptyState}</>
  }

  return (
    <>
      {/* Table mode — sm:+ only. */}
      <table className={`hidden w-full text-sm sm:table ${className}`}>
        <thead className="bg-surface-2 text-left text-xs uppercase tracking-wider text-muted">
          <tr>
            {columns.map((col) => (
              <th
                key={col.header}
                scope="col"
                className={
                  col.thClassName ??
                  `px-4 py-2${col.align === 'right' ? ' text-right' : ''}`
                }
              >
                {col.header}
              </th>
            ))}
            {actions && (
              <th scope="col" className="px-4 py-2 text-right">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((col) => (
                <td
                  key={col.header}
                  className={`px-4 py-3${col.align === 'right' ? ' text-right' : ''}`}
                >
                  {col.cell(row)}
                </td>
              ))}
              {actions && (
                <td className="px-4 py-3 text-right">{actions(row)}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Stacked mode — below sm only. */}
      <ul className={`divide-y divide-white/5 sm:hidden ${className}`}>
        {rows.map((row) => (
          <li key={rowKey(row)} className="space-y-2 p-4">
            {columns.map((col) => {
              if (col.primary) {
                return <div key={col.header}>{col.cell(row)}</div>
              }
              if (col.hideLabel) return null
              return (
                <div key={col.header} className="flex justify-between gap-3">
                  <span className="text-xs text-muted">{col.header}</span>
                  <span className="min-w-0 break-words text-right">
                    {col.cell(row)}
                  </span>
                </div>
              )
            })}
            {actions && (
              <div className="flex flex-wrap gap-2 pt-1">{actions(row)}</div>
            )}
          </li>
        ))}
      </ul>
    </>
  )
}
